import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { MaskedCurrency } from '../MaskedValue';
import { resolveAmountColor } from '../AmountDisplay';
import { baselineNetCents, everythingCents, netIfSpentCents } from '../../utils/dashboardN';
import FigureNote from './FigureNote';

/**
 * The Month view card's Bottom line (#2139, epic #2138). Current month: In
 * checking right now, Net, all budgets spent (the big number),
 * Baseline net. A closed month: Net at close (the big number),
 * Baseline net. The Ahead / Behind verdict pill and sentence retired with
 * #2179: the signed, colored big number says it. Every label-and-value pair
 * explains itself on hover after 2 seconds, on focus at once, and on tap on
 * a phone (FigureNote, #2179); no note text sits under a figure.
 *
 * From md up it sits in the card's left column (#2152, #2194): two columns,
 * In checking right now and Baseline net side by side for the current
 * month, Net at close and Baseline net for a closed one; the
 * current month then puts Net, all budgets spent across both columns under
 * a rule. Every wide cell is centered, and DashboardN centers the whole
 * block vertically beside Money in and out. Below md every figure is one
 * row, label and value, in the `figures` order.
 *
 * @param {object} props
 * @param {object} props.month               the `/flows` month entry
 * @param {boolean} props.isCurrent
 * @param {number} props.positiveBudgetCents `/tangible` positiveBudgetCents
 * @param {number} props.checkingCents       active Checking ledgers added up
 * @param {Object} props.strings             engine strings
 */
const BottomLine = ({ month, isCurrent, positiveBudgetCents, checkingCents, strings }) => {
  const theme = useTheme();
  const { palette, ds } = theme;
  const isWide = useMediaQuery(theme.breakpoints.up('md'));

  const lead = isCurrent ? netIfSpentCents(month, positiveBudgetCents) : everythingCents(month);

  const figures = isCurrent
    ? [
      { key: 'inChecking', cents: checkingCents, note: strings['dashboardN.inChecking.note'] },
      { key: 'netIfSpent', cents: lead, note: strings['dashboardN.netIfSpent.note'], lead: true },
      { key: 'baseline', cents: baselineNetCents(month), note: strings['dashboardN.baseline.noteNow'] },
    ]
    : [
      { key: 'everything', cents: lead, note: strings['dashboardN.everything.note'], lead: true },
      { key: 'baseline', cents: baselineNetCents(month), note: strings['dashboardN.baseline.noteClosed'] },
    ];

  // One figure: the label-and-value pair, the hover target, centered in its
  // cell from md up.
  const figureCell = (f, sx = {}) => {
    const label = strings[`dashboardN.${f.key}`];
    const color = resolveAmountColor(palette, f.lead ? 'total' : 'row', f.cents < 0, f.cents === 0);
    return (
      <Box key={f.key} data-testid={`bottom-line-${f.key}`} sx={{ minWidth: 0, ...sx }}>
        <FigureNote
          note={f.note}
          enterDelay={2000}
          sx={{
            display: 'flex',
            flexDirection: isWide ? 'column' : 'row',
            alignItems: isWide ? 'center' : 'baseline',
            textAlign: isWide ? 'center' : 'left',
            justifyContent: 'space-between',
            gap: isWide ? '2px' : `${ds.space[4]}px`,
            width: '100%',
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: '11px',
              letterSpacing: ds.font.letterUpper,
              textTransform: 'uppercase',
              color: palette.text.dim,
              minWidth: 0,
            }}
          >
            {label}
          </Typography>
          <MaskedCurrency
            value={f.cents / 100}
            style={{
              fontSize: `${f.lead ? ds.font.display : ds.font.hero}px`,
              fontWeight: ds.font.weightMedium,
              letterSpacing: ds.font.letterHero,
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1.2,
              color,
              whiteSpace: 'nowrap',
            }}
          />
        </FigureNote>
      </Box>
    );
  };

  const rule = `1px solid ${palette.divider}`;
  const figure = (key) => figures.find((f) => f.key === key);
  const right = { borderLeft: rule, pl: `${ds.space[8]}px` };
  const cells = !isWide
    ? figures.map((f) => figureCell(f))
    : isCurrent
      ? [
        figureCell(figure('inChecking')),
        figureCell(figure('baseline'), right),
        figureCell(figure('netIfSpent'), { gridColumn: '1 / -1', borderTop: rule, pt: `${ds.space[5]}px` }),
      ]
      : [
        figureCell(figure('everything')),
        figureCell(figure('baseline'), right),
      ];

  return (
    <Box
      data-testid="bottom-line"
      data-layout={isWide ? 'wide' : 'stacked'}
      sx={{
        display: 'grid',
        gridTemplateColumns: isWide ? 'repeat(2, minmax(0, 1fr))' : '1fr',
        columnGap: `${ds.space[8]}px`,
        rowGap: `${ds.space[4]}px`,
        py: `${ds.space[5]}px`,
      }}
    >
      {cells}
    </Box>
  );
};

export default BottomLine;
